import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import axiosInstance from '../services/axiosInstance';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [collaborator, setCollaborator] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('beworks_access_token');

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Load persisted context
      const storedCollaborator = localStorage.getItem('beworks_collaborator');
      const storedCustomer = localStorage.getItem('beworks_customer');
      const storedBusinesses = localStorage.getItem('beworks_businesses');

      if (storedCollaborator) {
        const collaboratorData = JSON.parse(storedCollaborator);
        setCollaborator(collaboratorData);

        // Reconstruct user from collaborator
        setUser({
          uuid: collaboratorData.uuid,
          name: collaboratorData.name,
          email: collaboratorData.email,
        });
      }

      if (storedCustomer) setCustomer(JSON.parse(storedCustomer));
      if (storedBusinesses) setBusinesses(JSON.parse(storedBusinesses));
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('beworks_access_token');
      localStorage.removeItem('beworks_refresh_token');
      localStorage.removeItem('beworks_collaborator');
      localStorage.removeItem('beworks_customer');
      localStorage.removeItem('beworks_businesses');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // Limpar cookies de sessão antes do login
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      const response = await authService.login(email, password);
      const { access, refresh } = response.data;

      localStorage.setItem('beworks_access_token', access);
      localStorage.setItem('beworks_refresh_token', refresh);

      try {
        const collaboratorResponse = await authService.getCollaboratorMe();
        const collaboratorData = collaboratorResponse.data;

        setUser({
          uuid: collaboratorData.uuid,
          name: collaboratorData.name,
          email: collaboratorData.email,
        });

        // Persist collaborator
        setCollaborator(collaboratorData);
        localStorage.setItem('beworks_collaborator', JSON.stringify(collaboratorData));

        // Persist customer (used for X-Customer-ID header)
        if (collaboratorData.customer) {
          setCustomer(collaboratorData.customer);
          localStorage.setItem('beworks_customer', JSON.stringify(collaboratorData.customer));
        }

        // Persist businesses
        if (collaboratorData.businesses) {
          setBusinesses(collaboratorData.businesses);
          localStorage.setItem('beworks_businesses', JSON.stringify(collaboratorData.businesses));
        }
      } catch (collaboratorError) {
        console.error('Failed to fetch collaborator data:', collaboratorError);
        // If collaborator fetch fails, login should fail
        throw collaboratorError;
      }

      navigate('/dashboard');

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);

      let errorMessage = 'Login failed. Please try again.';

      if (error.response?.status === 400 || error.response?.status === 401) {
        errorMessage = 'Invalid email or password.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have access to this customer.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const logout = () => {
    authService
      .logout()
      .catch((error) => {
        console.error('Logout error:', error);
      })
      .finally(() => {
        localStorage.removeItem('beworks_access_token');
        localStorage.removeItem('beworks_refresh_token');
        localStorage.removeItem('beworks_collaborator');
        localStorage.removeItem('beworks_customer');
        localStorage.removeItem('beworks_businesses');

        setUser(null);
        setCollaborator(null);
        setCustomer(null);
        setBusinesses([]);
        navigate('/login');
      });
  };

  const passwordResetConfirm = (uid, token, new_password1, new_password2) => {
    axiosInstance
      .post('/auth/password/reset/confirm/', {
        uid,
        token,
        new_password1,
        new_password2,
      })
      .catch((error) => {
        console.error('Change password error:', error);
      })
      .finally(() => {
        // navigate('/login');
      });
  };

  const value = {
    user,
    collaborator,
    customer,
    businesses,
    loading,
    login,
    logout,
    passwordResetConfirm
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
